/**
 * Products Showcase
 *  - Category loading via AJAX (no page navigation) with smooth height/fade
 *  - "Load more" pagination (12 at a time)
 *  - Client-side filter pills on the loaded set
 *  - AJAX add-to-cart + in-cart quantity control
 *  - Wishlist toggle (localStorage)
 *
 * Cart / qty / wishlist use event delegation so AJAX-injected cards work
 * without re-binding.
 */
(function () {
  const money = (cents) =>
    (cents / 100).toLocaleString('es-ES', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
    });

  class ProductsShowcase {
    constructor(root) {
      this.root = root;
      this.results = root.querySelector('[data-ps-results]');
      this.cartState = {};

      this.bindDelegation();
      this.bindCartSync();
      this.syncCart();
    }

    /* Sync with Dawn's cart whenever it changes anywhere (drawer, cart page…) */
    bindCartSync() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          // Ignore our own publishes (already handled locally).
          if (event && event.source === 'products-showcase') return;
          this.syncCart();
        });
      }
    }

    /* ---------------- Event delegation ---------------- */
    bindDelegation() {
      this.root.addEventListener('click', (e) => {
        const add = e.target.closest('.ps-card__add');
        const qtyBtn = e.target.closest('.ps-card__qty-btn');
        const wishlist = e.target.closest('.ps-card__wishlist');

        if (add) {
          const cartEl = add.closest('.ps-card__cart');
          this.changeQty(cartEl.dataset.variantId, this.getQty(cartEl.dataset.variantId) + 1, cartEl);
        } else if (qtyBtn) {
          const cartEl = qtyBtn.closest('.ps-card__cart');
          const delta = qtyBtn.dataset.action === 'increase' ? 1 : -1;
          this.changeQty(cartEl.dataset.variantId, this.getQty(cartEl.dataset.variantId) + delta, cartEl);
        } else if (wishlist) {
          this.toggleWishlist(wishlist);
        }
      });
    }

    /* Los filtros, la ordenación, la paginación y la carga por AJAX de categoría
       se eliminaron en sep-2026: este escaparate es un carrusel servido por el
       servidor, con un número fijo de productos. La barra de categorías que
       disparaba el AJAX no está en uso en ninguna plantilla. */

    /* ---------------- Cart ---------------- */
    getQty(variantId) {
      return this.cartState[variantId] || 0;
    }

    async syncCart() {
      try {
        await this.refreshState();
      } catch (e) {
        this.cartState = {};
      }
      this.root.querySelectorAll('.ps-card__cart').forEach((cartEl) => {
        this.renderCartState(cartEl, this.getQty(cartEl.dataset.variantId));
      });
      this.syncDrawerEmptyState();
    }

    /* Dawn keeps an is-empty class on the <cart-drawer> element (not the inner)
       that hides the items; its pub/sub re-render doesn't toggle it, so we
       mirror the real cart state here — exactly like product-form.js does. */
    syncDrawerEmptyState() {
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (!cart) return;
      const total = Object.values(this.cartState).reduce((a, b) => a + b, 0);
      cart.classList.toggle('is-empty', total === 0);
    }

    async refreshState() {
      const cart = await fetch('/cart.js').then((r) => r.json());
      this.cartState = {};
      cart.items.forEach((item) => {
        this.cartState[item.variant_id] = item.quantity;
      });
    }

    async changeQty(variantId, quantity, cartEl) {
      quantity = Math.max(0, quantity);

      // Tope de stock: nunca pedimos más unidades de las que hay. Antes se
      // enviaba igualmente y era Shopify quien devolvía el error.
      const max = cartEl && cartEl.dataset.max ? parseInt(cartEl.dataset.max, 10) : null;
      if (max !== null && quantity > max) quantity = max;

      const current = this.getQty(variantId);
      // Ya estábamos en el tope: no hay nada que enviar.
      if (quantity === current) return;
      const isAdd = current === 0 && quantity > 0;

      // Use Dawn's own cart element + the sections it wants to render, so we
      // update the drawer/bubble exactly like Dawn's product form does.
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sectionIds =
        cart && cart.getSectionsToRender ? cart.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'];
      const payload = { sections: sectionIds.join(','), sections_url: window.location.pathname };

      // Loader + botones desactivados mientras dura la petición.
      if (cartEl) cartEl.classList.add('is-loading');

      try {
        let res;
        if (isAdd) {
          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], ...payload }),
          });
        } else {
          res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: String(variantId), quantity, ...payload }),
          });
        }
        const json = await res.json();

        await this.refreshState();
        this.renderCartState(cartEl, this.getQty(variantId));

        // Al AÑADIR usamos el propio renderContents de Dawn (igual que la
        // página de detalle): actualiza el carrito y lo ABRE con su animación
        // de deslizamiento. Para los +/- (no es alta) actualizamos en silencio.
        if (cart && cart.getSectionsToRender) {
          if (isAdd && typeof cart.renderContents === 'function') {
            // Secuencia EXACTA de la página de detalle (product-form.js):
            // quitar is-empty del <cart-drawer> ANTES de renderContents, para
            // que el display ya esté estable y la animación de deslizamiento
            // no se interrumpa. renderContents actualiza el carrito y lo abre.
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            cart.renderContents(json);
          } else {
            this.applyCartSections(cart, json);
          }
        } else if (json.sections && json.sections['cart-icon-bubble']) {
          this.setCartBubble(json.sections['cart-icon-bubble']);
        }
      } catch (e) {
        /* noop */
      } finally {
        if (cartEl) cartEl.classList.remove('is-loading');
      }
    }

    /* Mirror Dawn's CartDrawer.renderContents (without forcing the drawer open):
       replace each section it renders, then fix the is-empty state. */
    applyCartSections(cart, json) {
      if (!json.sections) return;
      cart.getSectionsToRender().forEach((section) => {
        const target = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        const html = json.sections[section.id];
        if (!target || html == null) return;
        const source = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(section.selector || '.shopify-section');
        target.innerHTML = source ? source.innerHTML : html;
      });

      // Replacing #CartDrawer recreates the overlay, which loses its click-to-
      // close listener (bound in CartDrawer's constructor). Re-attach it, just
      // like Dawn's renderContents does.
      const overlay = document.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof cart.close === 'function') {
        overlay.addEventListener('click', cart.close.bind(cart));
      }

      this.syncDrawerEmptyState();
    }

    setCartBubble(html) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-cart-icon-bubble');
      bubble.innerHTML = el ? el.innerHTML : html;
    }

    renderCartState(cartEl, qty) {
      const qtyLabel = cartEl.querySelector('.ps-card__qty-label');

      // Con el stock agotado en el carrito, el + deja de estar disponible.
      const max = cartEl.dataset.max ? parseInt(cartEl.dataset.max, 10) : null;
      const increase = cartEl.querySelector('[data-action="increase"]');
      if (increase) increase.disabled = max !== null && qty >= max;
      if (qty > 0) {
        cartEl.classList.add('is-in-cart');
        const unit = parseInt(cartEl.closest('.ps-card').dataset.price, 10) || 0;
        const inCart = this.root.dataset.inCartLabel || 'in cart';
        qtyLabel.textContent = `${qty} ${inCart} — ${money(unit * qty)}`;
      } else {
        cartEl.classList.remove('is-in-cart');
      }
    }

    /* ---------------- Wishlist ---------------- */
    toggleWishlist(btn) {
      const KEY = 'destilo_wishlist';
      let list = [];
      try {
        list = JSON.parse(localStorage.getItem(KEY)) || [];
      } catch (e) {
        list = [];
      }
      const id = btn.dataset.productHandle;
      const idx = list.indexOf(id);
      if (idx > -1) {
        list.splice(idx, 1);
        btn.classList.remove('is-active');
      } else {
        list.push(id);
        btn.classList.add('is-active');
      }
      localStorage.setItem(KEY, JSON.stringify(list));
      /* Sube el cambio a la cuenta si hay sesión (assets/wishlist-sync.js). */
      if (window.DestiloWishlistSync) window.DestiloWishlistSync.push();
    }

    markWishlist() {
      let list = [];
      try {
        list = JSON.parse(localStorage.getItem('destilo_wishlist')) || [];
      } catch (e) {
        list = [];
      }
      this.root.querySelectorAll('.ps-card__wishlist').forEach((btn) => {
        btn.classList.toggle('is-active', list.includes(btn.dataset.productHandle));
      });
    }

    wait(ms) {
      return new Promise((res) => setTimeout(res, ms));
    }
  }

  function init() {
    document.querySelectorAll('[data-products-showcase]').forEach((el) => {
      const ps = new ProductsShowcase(el);
      ps.markWishlist();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
